'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { LocationPlaceValue } from '@repo/features/location';
import { useQuery } from '@repo/tanstack/query';
import { ResponsiveSheet, ResponsiveSheetBody, ResponsiveSheetContent, ResponsiveSheetHeader, type ResponsiveSheetPresentation, ResponsiveSheetTitle, Spinner } from '@repo/ui/components';
import { MapPin } from '@repo/ui/icons';
import { CreateLoadLocationForm } from './form';
import { useCreateLocationSheet } from './context';
type CreateLocationSheetProps = {
  presentation?: ResponsiveSheetPresentation;
};
export const CreateLocationSheet = t0 => {
  const $ = _c(35);
  const {
    presentation: t1
  } = t0;
  const presentation = t1 === undefined ? "default" : t1;
  const {
    closeCreateLocationSheet,
    formKey,
    isOpen,
    options
  } = useCreateLocationSheet();
  const api = useApi();
  const isPresented = (options?.presentation ?? "default") === presentation;
  const isActive = isOpen && isPresented;
  let t2;
  if ($[0] !== api.companies.getSimpleDetails || $[1] !== isActive || $[2] !== options?.companyId || $[3] !== options?.orgIdOverride) {
    t2 = api.companies.getSimpleDetails.queryOptions({
      input: {
        org_id: options?.orgIdOverride
      },
      path: {
        id: options?.companyId ?? 0
      }
    }, {
      enabled: !!options?.companyId && isActive
    });
    $[0] = api.companies.getSimpleDetails;
    $[1] = isActive;
    $[2] = options?.companyId;
    $[3] = options?.orgIdOverride;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: company
  } = useQuery(t2);
  let t3;
  if ($[5] !== api.companies.locations.get || $[6] !== isActive || $[7] !== options) {
    t3 = api.companies.locations.get.queryOptions({
      path: {
        companyId: options?.companyId ?? 0,
        id: options?.mode === "edit" ? options.locationId : 0
      }
    }, {
      enabled: options?.mode === "edit" && !!options?.companyId && isActive
    });
    $[5] = api.companies.locations.get;
    $[6] = isActive;
    $[7] = options;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data: existingLocation,
    isPending: isGetPending
  } = useQuery(t3);
  const title = options?.mode === "edit" ? "Edit location" : "New location";
  if (!isPresented) {
    return null;
  }
  let t4;
  if ($[9] !== closeCreateLocationSheet) {
    t4 = open => !open && closeCreateLocationSheet();
    $[9] = closeCreateLocationSheet;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MapPin data-sentry-element="MapPin" data-sentry-source-file="sheet.tsx" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== title) {
    t6 = <ResponsiveSheetTitle className="flex items-center gap-3 text-3xl font-bold" data-sentry-element="ResponsiveSheetTitle" data-sentry-source-file="sheet.tsx">{t5} {title}</ResponsiveSheetTitle>;
    $[12] = title;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== company) {
    t7 = company && <p className="text-sm text-muted-foreground">{company.title}</p>;
    $[14] = company;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <ResponsiveSheetHeader className="gap-4 p-0" data-sentry-element="ResponsiveSheetHeader" data-sentry-source-file="sheet.tsx">{t6}{t7}</ResponsiveSheetHeader>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== closeCreateLocationSheet || $[20] !== existingLocation || $[21] !== formKey || $[22] !== isGetPending || $[23] !== options) {
    t9 = options ? options.mode === "edit" && isGetPending ? <div className="flex justify-center py-8"><Spinner className="size-8" /></div> : <CreateLoadLocationForm companyId={options.companyId} existingLocation={options.mode === "edit" ? existingLocation : undefined} initialSearchQuery={options.mode === "create" ? options.initialSearchQuery : undefined} initialValues={options.mode === "create" ? options.initialValues : undefined} key={options.mode === "edit" ? `${formKey}-${options.locationId}-${existingLocation?.updated_at?.getTime() ?? ""}` : formKey} locationId={options.mode === "edit" ? options.locationId : undefined} mode={options.mode} onCancel={closeCreateLocationSheet} onCreated={options.mode === "create" ? place => {
      options.onCreated?.(place);
      closeCreateLocationSheet();
    } : undefined} onUpdated={options.mode === "edit" ? place_0 => {
      options.onUpdated?.(place_0);
      closeCreateLocationSheet();
    } : undefined} orgIdOverride={options.orgIdOverride} /> : null;
    $[19] = closeCreateLocationSheet;
    $[20] = existingLocation;
    $[21] = formKey;
    $[22] = isGetPending;
    $[23] = options;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t9) {
    t10 = <ResponsiveSheetBody data-sentry-element="ResponsiveSheetBody" data-sentry-source-file="sheet.tsx">{t9}</ResponsiveSheetBody>;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t8) {
    t11 = <ResponsiveSheetContent className="gap-8 overflow-hidden p-6 max-lg:w-full! sm:max-w-sm" data-sentry-element="ResponsiveSheetContent" data-sentry-source-file="sheet.tsx">{t8}{t10}</ResponsiveSheetContent>;
    $[27] = t10;
    $[28] = t8;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== isActive || $[31] !== presentation || $[32] !== t11 || $[33] !== t4) {
    t12 = <ResponsiveSheet onOpenChange={t4} open={isActive} presentation={presentation} data-sentry-element="ResponsiveSheet" data-sentry-component="CreateLocationSheet" data-sentry-source-file="sheet.tsx">{t11}</ResponsiveSheet>;
    $[30] = isActive;
    $[31] = presentation;
    $[32] = t11;
    $[33] = t4;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};
export const CreateLocationSheetNestedDialogOutlet = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CreateLocationSheet presentation="nested-dialog" data-sentry-element="CreateLocationSheet" data-sentry-component="CreateLocationSheetNestedDialogOutlet" data-sentry-source-file="sheet.tsx" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};